const ALLOWED_TAGS = new Set([
  "b",
  "blockquote",
  "br",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "i",
  "li",
  "ol",
  "p",
  "span",
  "strong",
  "u",
  "ul",
]);

const BLOCKED_ELEMENT_PATTERN =
  /<(script|style|iframe|object|embed|svg|math)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;
const COMMENT_PATTERN = /<!--[\s\S]*?-->/g;
const TAG_PATTERN = /<\s*(\/?)\s*([a-z][\w-]*)([^>]*)>/gi;
const SAFE_COLOR_PATTERN =
  /^(?:#[0-9a-f]{3,8}|(?:rgb|rgba|hsl|hsla)\([0-9.,%\s-]+\)|transparent|[a-z]+)$/i;
const ALLOWED_CLASS_NAMES = new Set(["ql-align-justify"]);

const getSafeClassName = (attributes: string): string | null => {
  const classMatch = attributes.match(/\bclass\s*=\s*(["'])(.*?)\1/i);
  const classNames = classMatch?.[2].split(/\s+/).filter(Boolean) ?? [];
  return classNames.length > 0 &&
    classNames.every((className) => ALLOWED_CLASS_NAMES.has(className))
    ? classNames.join(" ")
    : null;
};

const getSafeStyle = (attributes: string): string | null => {
  const styleMatch = attributes.match(/\bstyle\s*=\s*(["'])(.*?)\1/i);
  if (!styleMatch) return null;

  const safeDeclarations = styleMatch[2]
    .split(";")
    .map((declaration) => declaration.trim())
    .filter(Boolean)
    .flatMap((declaration) => {
      const separatorIndex = declaration.indexOf(":");
      if (separatorIndex < 0) return [];

      const property = declaration
        .slice(0, separatorIndex)
        .trim()
        .toLowerCase();
      const value = declaration.slice(separatorIndex + 1).trim();
      if (
        (property !== "color" && property !== "background-color") ||
        !SAFE_COLOR_PATTERN.test(value)
      ) {
        return [];
      }

      return `${property}: ${value}`;
    });

  return safeDeclarations.length > 0 ? safeDeclarations.join("; ") : null;
};

export const sanitizeChannelDescription = (html: string): string =>
  html
    .replace(BLOCKED_ELEMENT_PATTERN, "")
    .replace(COMMENT_PATTERN, "")
    .replace(
      TAG_PATTERN,
      (_match, closing: string, rawTagName: string, attributes: string) => {
        const tagName = rawTagName.toLowerCase();
        if (!ALLOWED_TAGS.has(tagName)) return "";
        if (tagName === "br") return closing ? "" : "<br>";
        if (closing) return `</${tagName}>`;

        const safeAttributes: string[] = [];
        if (tagName === "p") {
          const className = getSafeClassName(attributes);
          if (className) safeAttributes.push(`class="${className}"`);
        }

        const style = getSafeStyle(attributes);
        if (style) safeAttributes.push(`style="${style}"`);

        return `<${tagName}${
          safeAttributes.length > 0 ? ` ${safeAttributes.join(" ")}` : ""
        }>`;
      }
    );
